import { Moon, Search, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { IconButton } from '@/components/ui/primitives'
import { useResolvedTheme } from '@/lib/theme'
import { usePreferences } from '@/store/preferences'
import { useUi } from '@/store/ui'

export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation()
  const theme = useResolvedTheme()
  const setTheme = usePreferences((s) => s.setTheme)
  const next = theme === 'dark' ? 'light' : 'dark'
  return (
    <IconButton label={t(`settings.themes.${next}`)} onClick={() => setTheme(next)} className={className}>
      {theme === 'dark' ? <Sun className="size-[1.15rem]" /> : <Moon className="size-[1.15rem]" />}
    </IconButton>
  )
}

export function LanguageToggle({ className }: { className?: string }) {
  const language = usePreferences((s) => s.language)
  const setLanguage = usePreferences((s) => s.setLanguage)
  const next = language === 'ar' ? 'en' : 'ar'
  return (
    <button
      onClick={() => setLanguage(next)}
      lang={next}
      aria-label={next === 'en' ? 'English' : 'العربية'}
      className={clsx(
        'inline-flex h-10 min-w-10 items-center justify-center rounded-xl px-2.5 text-sm font-semibold text-muted transition hover:bg-surface-2 hover:text-fg',
        className,
      )}
    >
      {next === 'en' ? 'EN' : 'ع'}
    </button>
  )
}

export function SearchTrigger({ variant }: { variant: 'field' | 'icon' }) {
  const { t } = useTranslation()
  const open = useUi((s) => s.setSearchOpen)
  if (variant === 'icon') {
    return (
      <IconButton label={t('nav.search')} onClick={() => open(true)}>
        <Search className="size-[1.15rem]" />
      </IconButton>
    )
  }
  return (
    <button
      onClick={() => open(true)}
      className="group flex h-11 w-full max-w-md items-center gap-3 rounded-xl border border-line bg-surface-1/70 px-3.5 text-sm text-subtle transition hover:border-line-strong hover:text-muted"
    >
      <Search className="size-4" aria-hidden />
      <span className="flex-1 text-start">{t('nav.search')}</span>
      <kbd className="ltr hidden rounded-md border border-line px-1.5 py-0.5 font-sans text-[0.7rem] lg:inline">
        Ctrl K
      </kbd>
    </button>
  )
}
